/**
 * The designer's corrections to a scan, and the plan they produce.
 *
 * Asked for on 2026-09-11: "like Polycam's floor-plan editor — fix what the
 * scan got wrong": move, resize, delete and add walls, straighten them, give
 * them a thickness; add, slide, resize and delete doors, windows and openings;
 * rename rooms.
 *
 * THE CAPTURE IS EVIDENCE AND IS NEVER CHANGED. RoomPlan's capture is what the
 * sensor saw on the day, stored write-once with its hash (migration 0026). The
 * corrections are a separate EDIT LAYER — one record per corrected element,
 * saved with who and when (migration 0039) — and the plan everybody works
 * from is computed:
 *
 *     effective plan = applyPlanEdits(capture, edits)
 *
 * a pure function, so the drawing on screen, the MCS design check in the API,
 * the scan -> heat-loss conversion, exports and the room schedule can all ask
 * the same question and get the same walls. Take the edit layer away and the
 * capture is exactly as it was.
 *
 * THE OUTPUT IS A CAPTURE. `applyPlanEdits` returns a `RoomPlanCapture` of the
 * same shape it was given, with corrected transforms and dimensions, so every
 * consumer that already reads a capture — `scanRoomGroups`, `scanPlanRooms`,
 * `convertCapture`, the 3D viewer — reads the corrected one with no second
 * code path. Three things a capture has no field for travel beside it in
 * `overrides` (see `ScanGroupOverrides`): stable element labels, room names,
 * and wall thickness for the drawing.
 *
 * ONE RECORD PER ELEMENT, NOT A LOG. A record says what the element IS now
 * ("this wall runs from here to here, 100 mm thick"), not what was done to it,
 * so the records apply in any order and "revert to scan" is deleting one.
 * Undo and redo are the portal's, within a session, over these records.
 *
 * WHAT FOLLOWS WHAT. A window sits in a wall: move or turn the wall and its
 * windows, doors and openings go with it, keeping their distance from whichever
 * end of the wall did not move (see `relocateOnWall`). Delete the wall and the
 * openings in it go too — an opening with no wall is not a thing. The plan's
 * radiators and outdoor unit are the portal's to move (they are separate
 * records, migration 0032); they use the same `relocateOnWall`.
 *
 * FRAME. Plan metres, exactly as `scan-plan.ts` projects: plan x = world X,
 * plan y = world Z. Heights are world Y, and a wall's or opening's height is
 * kept or set relative to its own bottom edge, which a plan edit never moves.
 */

import type {
  CapturedRoom,
  CapturedStructure,
  CapturedSurface,
  CapturedSurfaceKind,
  RoomPlanCapture,
} from "./roomplan.js";
import { capturedRoomRef, capturedRooms } from "./roomplan.js";
import { mergeRoomsInto, settledRooms, splitRoomAlong } from "./room-split.js";

/**
 * Merges, then splits, as the room corrections ask. A merge takes the room
 * it is recorded on into `mergeInto` (same storey only); a split divides the
 * room along its line (room-split.ts). Anything that cannot apply is reported
 * in `skipped`, and the plan is drawn without it.
 */
function applyRoomStructure(
  rooms: readonly CapturedRoom[],
  edits: readonly Extract<PlanEdit, { targetKind: "room" }>[],
  status: Map<string, PlanEditStatus>,
  skipped: SkippedPlanEdit[],
): CapturedRoom[] {
  const out = [...rooms];
  const find = (ref: string) => out.findIndex((r, i) => capturedRoomRef(r, i) === ref);
  for (const edit of edits) {
    const into = edit.data.mergeInto;
    if (into === undefined) continue;
    const b = find(edit.targetId);
    const a = find(into);
    if (b < 0) {
      skipped.push({ edit, reason: "There is no room with this id in the scan." });
      continue;
    }
    if (a < 0 || a === b) {
      skipped.push({ edit, reason: "The room it was to be merged into is not on the plan." });
      continue;
    }
    if ((out[a]!.story ?? 0) !== (out[b]!.story ?? 0)) {
      skipped.push({ edit, reason: "Rooms on different storeys cannot be merged." });
      continue;
    }
    out[a] = mergeRoomsInto(out[a]!, out[b]!, into);
    out.splice(b, 1);
    status.set(edit.targetId, "modified");
  }
  for (const edit of edits) {
    const line = edit.data.split;
    if (line === undefined) continue;
    const i = find(edit.targetId);
    if (i < 0) {
      skipped.push({ edit, reason: "There is no room with this id in the scan." });
      continue;
    }
    const halves = splitRoomAlong(out[i]!, edit.targetId, line);
    if (!halves) {
      skipped.push({
        edit,
        reason: "The line does not divide the room in two: draw it across the room, from one wall to another.",
      });
      continue;
    }
    out.splice(i, 1, ...halves);
    status.set(edit.targetId, "modified");
  }
  return out;
}
import { scanRoomGroups, type ScanGroupOverrides, type ScanRoomGroup } from "./scan-elements.js";
import { scanPlanRooms, type PlanRoom } from "./scan-plan.js";
import { heightAxisOf, isUsableTransform, median, surfaceArea, translationOf, widthAxisOf } from "./spatial.js";

/** A point on the plan, metres (x = world X, y = world Z). */
export interface PlanEditPoint {
  readonly x: number;
  readonly y: number;
}

export const PLAN_EDIT_TARGET_KINDS = ["wall", "opening", "room"] as const;
export type PlanEditTargetKind = (typeof PLAN_EDIT_TARGET_KINDS)[number];

export const PLAN_EDIT_ACTIONS = ["modify", "add", "delete"] as const;
export type PlanEditAction = (typeof PLAN_EDIT_ACTIONS)[number];

export const PLAN_OPENING_KINDS = ["door", "window", "opening"] as const;
export type PlanOpeningKind = (typeof PLAN_OPENING_KINDS)[number];

/**
 * The bounds every edit is held to — by the API before it is stored, and by
 * the editor's fields before it is sent. Generous: they catch a unit slip
 * (millimetres typed as metres), not a design choice.
 */
export const PLAN_EDIT_LIMITS = {
  coordinateM: 10_000,
  wallLengthM: { min: 0.05, max: 100 },
  wallThicknessM: { min: 0.02, max: 1.5 },
  wallHeightM: { min: 0.3, max: 10 },
  openingWidthM: { min: 0.1, max: 20 },
  openingHeightM: { min: 0.1, max: 6 },
  sillHeightM: { min: 0, max: 6 },
  offsetM: { min: 0, max: 100 },
  roomLabelLength: 80,
  targetIdLength: 200,
} as const;

/** A wall as corrected: where it runs, and optionally how thick and tall it is. */
export interface WallModifyData {
  /** Both ends, or neither: the wall's whole run on the plan. */
  readonly start?: PlanEditPoint;
  readonly end?: PlanEditPoint;
  readonly thicknessM?: number;
  readonly heightM?: number;
}

/** A wall the scan missed. */
export interface WallAddData {
  /** `capturedRoomRef` of the room it belongs to. */
  readonly roomRef: string;
  readonly start: PlanEditPoint;
  readonly end: PlanEditPoint;
  readonly thicknessM?: number;
  /** Defaults to the room's tallest wall (RoomPlan has no ceiling), else 2.4 m. */
  readonly heightM?: number;
}

/** A window, door or opening as corrected. Every field optional. */
export interface OpeningModifyData {
  /** Move it into another wall. */
  readonly wallId?: string;
  /** Distance along the wall from the wall's start to the opening's centre. */
  readonly offsetM?: number;
  readonly widthM?: number;
  readonly heightM?: number;
  /** Bottom edge above the bottom of the wall it is in (the floor). */
  readonly sillHeightM?: number;
}

/** A window, door or opening the scan missed. */
export interface OpeningAddData {
  readonly kind: PlanOpeningKind;
  readonly wallId: string;
  readonly offsetM: number;
  readonly widthM: number;
  readonly heightM: number;
  readonly sillHeightM: number;
}

/**
 * A room's corrections, any of them: its name; a line to split it along (the
 * plan's "Split room" — the halves are `<room>~0`, left of the line from its
 * start, and `<room>~1`); or the room it was merged into (the plan's "Merge
 * rooms" — recorded on the room that goes). See room-split.ts.
 */
export interface RoomModifyData {
  readonly label?: string;
  readonly split?: { readonly start: PlanEditPoint; readonly end: PlanEditPoint };
  readonly mergeInto?: string;
}

export type PlanEditShape =
  | { readonly targetKind: "wall"; readonly action: "modify"; readonly data: WallModifyData }
  | { readonly targetKind: "wall"; readonly action: "add"; readonly data: WallAddData }
  | { readonly targetKind: "wall"; readonly action: "delete"; readonly data?: Record<string, never> }
  | { readonly targetKind: "opening"; readonly action: "modify"; readonly data: OpeningModifyData }
  | { readonly targetKind: "opening"; readonly action: "add"; readonly data: OpeningAddData }
  | { readonly targetKind: "opening"; readonly action: "delete"; readonly data?: Record<string, never> }
  | { readonly targetKind: "room"; readonly action: "modify"; readonly data: RoomModifyData };

/**
 * One correction. `targetId` is the element's own identifier — RoomPlan's for
 * a captured wall or opening, `capturedRoomRef` for a room, and a new id the
 * editor chose for an added element.
 */
export type PlanEdit = PlanEditShape & {
  readonly targetId: string;
  /** RoomPlan storey index of the element; for listing, not for applying. */
  readonly storey: number;
};

/** The key an edit is stored under: one record per element. */
export function planEditKey(edit: Pick<PlanEdit, "targetKind" | "targetId">): string {
  return `${edit.targetKind}:${edit.targetId}`;
}

export type PlanEditStatus = "modified" | "added";

export interface PlanEditRemoval {
  readonly id: string;
  readonly kind: CapturedSurfaceKind;
  /** "deleted" by its own edit, or removed because the wall it was in was. */
  readonly reason: "deleted" | "host-deleted";
}

export interface SkippedPlanEdit {
  readonly edit: PlanEdit;
  readonly reason: string;
}

/** The capture with the edit layer applied, and an account of what changed. */
export interface EffectivePlan {
  /** The corrected capture, same shape as the input. The input itself is untouched. */
  readonly capture: RoomPlanCapture;
  /** Pass to `scanRoomGroups` (or use `effectiveRoomGroups`). */
  readonly overrides: ScanGroupOverrides;
  /** Elements (and room refs) changed or added by their own edit. */
  readonly status: ReadonlyMap<string, PlanEditStatus>;
  /**
   * Elements not edited themselves but moved with the walls: openings in a
   * moved wall, and a room's floor redrawn to its corrected walls.
   */
  readonly followed: ReadonlySet<string>;
  readonly removed: readonly PlanEditRemoval[];
  /** Edits that could not apply — their element is not in this capture, say — and why. */
  readonly skipped: readonly SkippedPlanEdit[];
  /** Room names set by the designer, by `capturedRoomRef`. */
  readonly roomLabels: ReadonlyMap<string, string>;
  /**
   * For every room whose walls were moved, added or deleted: its floor
   * redrawn to the walls (and the area that gives), or kept as scanned and
   * why. Rooms whose walls' runs are as captured are not listed.
   */
  readonly floors: readonly FloorOutcome[];
}

/** What became of a room's floor when its walls were corrected. */
export type FloorOutcome =
  | { readonly roomRef: string; readonly outcome: "redrawn"; readonly areaM2: number }
  | { readonly roomRef: string; readonly outcome: "as-scanned"; readonly reason: string };

// ------------------------------------------------------ plan geometry ----

export interface PlanLine {
  readonly start: PlanEditPoint;
  readonly end: PlanEditPoint;
}

const EPS_M = 0.001;

function sub(a: PlanEditPoint, b: PlanEditPoint): PlanEditPoint {
  return { x: a.x - b.x, y: a.y - b.y };
}
function lengthOf(v: PlanEditPoint): number {
  return Math.hypot(v.x, v.y);
}
function dist(a: PlanEditPoint, b: PlanEditPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
function unit(line: PlanLine): PlanEditPoint | undefined {
  const d = sub(line.end, line.start);
  const l = lengthOf(d);
  return l > 1e-9 ? { x: d.x / l, y: d.y / l } : undefined;
}
function clamp(value: number, lo: number, hi: number): number {
  return Math.min(Math.max(value, lo), hi);
}

/** A line's length, metres. */
export function planLineLengthM(line: PlanLine): number {
  return dist(line.start, line.end);
}

/** A line's bearing on the plan, degrees, atan2(dy, dx) — clockwise on screen. */
export function planLineAngleDeg(line: PlanLine): number {
  return (Math.atan2(line.end.y - line.start.y, line.end.x - line.start.x) * 180) / Math.PI;
}

/**
 * Where a wall surface runs on the plan: its centre plus and minus half its
 * width along the plan projection of its width axis. The same arithmetic as
 * `scan-plan.ts`'s `segmentOf`, on a raw surface.
 */
export function surfacePlanLine(surface: CapturedSurface): PlanLine | undefined {
  if (!isUsableTransform(surface.transform)) return undefined;
  const axis = widthAxisOf(surface.transform);
  const l = Math.hypot(axis.x, axis.z);
  if (!(l > 1e-6)) return undefined;
  const width = surface.dimensions?.[0];
  if (typeof width !== "number" || !(width > 0)) return undefined;
  const c = translationOf(surface.transform);
  const a = { x: axis.x / l, y: axis.z / l };
  const h = width / 2;
  return {
    start: { x: c.x - a.x * h, y: c.z - a.y * h },
    end: { x: c.x + a.x * h, y: c.z + a.y * h },
  };
}

/**
 * Where something on a wall goes when the wall changes from `before` to
 * `after`, and how far it turns.
 *
 * Its distance along the wall is kept from whichever END DID NOT MOVE: shorten
 * a wall from its far end and a window near its start stays where it was on
 * site. Moved or turned whole (same length), the distance from the start is
 * kept. Both ends moved and the length changed: kept in proportion. Its
 * distance off the wall line (a radiator's depth, a door on the far face of a
 * party wall) is kept on the same side. `halfLengthM` keeps a thing of that
 * half-length inside the new wall (centred on a wall shorter than itself).
 *
 * `turnDeg` is the change in the wall's bearing — what a thing on it adds to
 * its own rotation (clockwise on screen, as the plan's objects rotate).
 */
export function relocateOnWall(
  point: PlanEditPoint,
  before: PlanLine,
  after: PlanLine,
  halfLengthM = 0,
): { readonly point: PlanEditPoint; readonly turnDeg: number; readonly alongM: number } {
  const a0 = unit(before);
  const a1 = unit(after);
  if (!a0 || !a1) return { point, turnDeg: 0, alongM: 0 };
  const L0 = planLineLengthM(before);
  const L1 = planLineLengthM(after);
  const rel = sub(point, before.start);
  const s = rel.x * a0.x + rel.y * a0.y;
  const off = rel.x * -a0.y + rel.y * a0.x;

  let s1: number;
  if (dist(before.start, after.start) < EPS_M) s1 = s;
  else if (dist(before.end, after.end) < EPS_M) s1 = L1 - (L0 - s);
  else if (Math.abs(L1 - L0) < EPS_M) s1 = s;
  else s1 = L0 > 0 ? (s * L1) / L0 : L1 / 2;
  s1 = L1 >= 2 * halfLengthM ? clamp(s1, halfLengthM, L1 - halfLengthM) : L1 / 2;

  const n1 = { x: -a1.y, y: a1.x };
  const turn = planLineAngleDeg(after) - planLineAngleDeg(before);
  return {
    point: {
      x: after.start.x + a1.x * s1 + n1.x * off,
      y: after.start.y + a1.y * s1 + n1.y * off,
    },
    turnDeg: ((turn % 360) + 540) % 360 - 180,
    alongM: s1,
  };
}

/** Distance along `line` from its start to the foot of `point`, metres. */
export function alongLineM(point: PlanEditPoint, line: PlanLine): number {
  const a = unit(line);
  if (!a) return 0;
  const rel = sub(point, line.start);
  return rel.x * a.x + rel.y * a.y;
}

/** The point `alongM` metres from `line`'s start, on the line. */
export function pointAlongLine(line: PlanLine, alongM: number): PlanEditPoint {
  const a = unit(line) ?? { x: 1, y: 0 };
  return { x: line.start.x + a.x * alongM, y: line.start.y + a.y * alongM };
}

/** The same line at a new length, keeping `anchor` where it is. */
export function resizedLine(
  line: PlanLine,
  lengthM: number,
  anchor: "start" | "centre" | "end" = "start",
): PlanLine {
  const a = unit(line) ?? { x: 1, y: 0 };
  if (anchor === "start") {
    return { start: line.start, end: { x: line.start.x + a.x * lengthM, y: line.start.y + a.y * lengthM } };
  }
  if (anchor === "end") {
    return { start: { x: line.end.x - a.x * lengthM, y: line.end.y - a.y * lengthM }, end: line.end };
  }
  const c = { x: (line.start.x + line.end.x) / 2, y: (line.start.y + line.end.y) / 2 };
  const h = lengthM / 2;
  return {
    start: { x: c.x - a.x * h, y: c.y - a.y * h },
    end: { x: c.x + a.x * h, y: c.y + a.y * h },
  };
}

/**
 * The storey's main direction, degrees in [0, 90): the length-weighted mean
 * of every wall's bearing folded onto a quarter turn (the angle x4 trick, so
 * a wall and the one at right angles to it vote for the same axis). A house
 * is drawn square to itself, not to wherever north was when the phone started.
 */
export function dominantAxisDeg(lines: readonly PlanLine[]): number {
  let sx = 0;
  let sy = 0;
  for (const line of lines) {
    const l = planLineLengthM(line);
    if (!(l > 0)) continue;
    const t = (planLineAngleDeg(line) * Math.PI) / 180;
    sx += l * Math.cos(4 * t);
    sy += l * Math.sin(4 * t);
  }
  if (sx === 0 && sy === 0) return 0;
  const deg = (Math.atan2(sy, sx) * 180) / Math.PI / 4;
  return ((deg % 90) + 90) % 90;
}

/**
 * The wall turned about its centre onto the nearest `stepDeg` from the
 * storey's main axis — 45° by default, so a square room's walls go square and
 * a bay's go to 45°. Its length is kept.
 */
export function straightenedLine(line: PlanLine, axisDeg: number, stepDeg = 45): PlanLine {
  const bearing = planLineAngleDeg(line);
  const snapped = axisDeg + Math.round((bearing - axisDeg) / stepDeg) * stepDeg;
  const t = (snapped * Math.PI) / 180;
  const h = planLineLengthM(line) / 2;
  const c = { x: (line.start.x + line.end.x) / 2, y: (line.start.y + line.end.y) / 2 };
  return {
    start: { x: c.x - Math.cos(t) * h, y: c.y - Math.sin(t) * h },
    end: { x: c.x + Math.cos(t) * h, y: c.y + Math.sin(t) * h },
  };
}

// ------------------------------------------------------ transforms ----

type Matrix = number[];

function matrixOf(surface: CapturedSurface): Matrix {
  return [...(surface.transform as readonly number[])];
}

/** Turns a matrix's three axes about world Y by the plan rotation (c, s). */
function turned(m: Matrix, c: number, s: number): Matrix {
  const out = [...m];
  for (const col of [0, 4, 8]) {
    const x = m[col] ?? 0;
    const z = m[col + 2] ?? 0;
    out[col] = c * x - s * z;
    out[col + 2] = s * x + c * z;
  }
  return out;
}

function placed(m: Matrix, x: number, y: number, z: number): Matrix {
  const out = [...m];
  out[12] = x;
  out[13] = y;
  out[14] = z;
  return out;
}

/** The rotation (cos, sin) that carries direction `a0` onto `a1`, in the plan. */
function rotationBetween(a0: PlanEditPoint, a1: PlanEditPoint): { c: number; s: number } {
  return { c: a0.x * a1.x + a0.y * a1.y, s: a0.x * a1.y - a0.y * a1.x };
}

function dimensionsWith(surface: CapturedSurface, patch: { 0?: number; 1?: number; 2?: number }): number[] {
  const dims = [...(surface.dimensions ?? [])];
  while (dims.length < 3) dims.push(0);
  for (const i of [0, 1, 2] as const) {
    const v = patch[i];
    if (v !== undefined) dims[i] = v;
  }
  return dims;
}

/** World Y of a standing surface's bottom edge: its centre less half its height, down its height axis. */
function bottomY(surface: CapturedSurface): number | undefined {
  if (!isUsableTransform(surface.transform)) return undefined;
  const h = surface.dimensions?.[1];
  if (typeof h !== "number" || !(h > 0)) return undefined;
  const up = heightAxisOf(surface.transform);
  const len = Math.hypot(up.x, up.y, up.z);
  if (!(len > 1e-6) || Math.abs(up.y) / len < 0.99) return undefined;
  // Vertical either way up, so the lowest edge is half the height below the centre.
  return translationOf(surface.transform).y - h / 2;
}

function heightOfSurface(surface: CapturedSurface): number {
  const h = surface.dimensions?.[1];
  return typeof h === "number" && h > 0 ? h : 0;
}

// ------------------------------------------------------- the engine ----

interface MutableRoom {
  readonly original: CapturedRoom;
  readonly ref: string;
  readonly story: number;
  walls: CapturedSurface[];
  windows: CapturedSurface[];
  doors: CapturedSurface[];
  openings: CapturedSurface[];
  floors: CapturedSurface[];
  touched: boolean;
  /** A wall of this room was moved, added or deleted: its floor is redrawn (or said to be stale). */
  runsChanged: boolean;
}

type OpeningArray = "windows" | "doors" | "openings";
const OPENING_ARRAY: Readonly<Record<PlanOpeningKind, OpeningArray>> = {
  window: "windows",
  door: "doors",
  opening: "openings",
};
const ARRAY_KIND: Readonly<Record<OpeningArray, PlanOpeningKind>> = {
  windows: "window",
  doors: "door",
  openings: "opening",
};

const DEFAULT_WALL_HEIGHT_M = 2.4;

/**
 * The effective plan: `capture` with `edits` applied. Pure: neither argument
 * is changed, and the same two in give the same plan out. With no edits the
 * capture is returned as it came.
 *
 * Order, so that records never depend on the order they are stored in:
 * walls deleted, then walls corrected (their openings following), then walls
 * added; then openings deleted, corrected and added (onto the walls as they
 * now are); then room names. Two records for one element: the later wins.
 */
export function applyPlanEdits(
  capture: RoomPlanCapture,
  edits: readonly PlanEdit[],
): EffectivePlan {
  const sourceRooms = capturedRooms(capture);

  // Every surface keeps the name the scan gave it, even when a wall before it
  // in the room is deleted — "Wall 3" must not become "Wall 2" under a
  // designer who is looking at it.
  const elementLabels = new Map<string, string>();
  sourceRooms.forEach((room) => {
    const name = (list: readonly CapturedSurface[] | undefined, singular: string): void =>
      (list ?? []).forEach((s, i) => elementLabels.set(s.identifier, `${singular} ${i + 1}`));
    name(room.walls, "Wall");
    name(room.windows, "Window");
    name(room.doors, "Door");
    name(room.openings, "Opening");
    name(room.floors, "Floor");
  });

  if (edits.length === 0) {
    return {
      capture,
      overrides: { elementLabels },
      status: new Map(),
      followed: new Set(),
      removed: [],
      skipped: [],
      roomLabels: new Map(),
      floors: [],
    };
  }

  const latest = new Map<string, PlanEdit>();
  for (const edit of edits) latest.set(planEditKey(edit), edit);
  const all = [...latest.values()];
  const of = <K extends PlanEditTargetKind, A extends PlanEditAction>(kind: K, action: A) =>
    all.filter(
      (e): e is Extract<PlanEdit, { targetKind: K; action: A }> =>
        e.targetKind === kind && e.action === action,
    );

  const rooms: MutableRoom[] = sourceRooms.map((room, index) => ({
    original: room,
    ref: capturedRoomRef(room, index),
    story: room.story ?? 0,
    walls: [...(room.walls ?? [])],
    windows: [...(room.windows ?? [])],
    doors: [...(room.doors ?? [])],
    openings: [...(room.openings ?? [])],
    floors: [...(room.floors ?? [])],
    touched: false,
    runsChanged: false,
  }));

  const status = new Map<string, PlanEditStatus>();
  const followed = new Set<string>();
  const removed: PlanEditRemoval[] = [];
  const skipped: SkippedPlanEdit[] = [];
  const roomLabels = new Map<string, string>();
  const thicknessM = new Map<string, number>();

  const findWall = (id: string): { room: MutableRoom; index: number } | undefined => {
    for (const room of rooms) {
      const index = room.walls.findIndex((w) => w.identifier === id);
      if (index >= 0) return { room, index };
    }
    return undefined;
  };
  const findOpening = (
    id: string,
  ): { room: MutableRoom; array: OpeningArray; index: number } | undefined => {
    for (const room of rooms) {
      for (const array of ["windows", "doors", "openings"] as const) {
        const index = room[array].findIndex((o) => o.identifier === id);
        if (index >= 0) return { room, array, index };
      }
    }
    return undefined;
  };
  const replaceWall = (room: MutableRoom, index: number, next: CapturedSurface): void => {
    room.walls = room.walls.map((w, i) => (i === index ? next : w));
    room.touched = true;
  };
  const replaceOpening = (room: MutableRoom, array: OpeningArray, index: number, next: CapturedSurface): void => {
    room[array] = room[array].map((o, i) => (i === index ? next : o));
    room.touched = true;
  };

  // ---- walls deleted
  const deletedWalls = new Set<string>();
  for (const edit of of("wall", "delete")) {
    const at = findWall(edit.targetId);
    if (!at) {
      skipped.push({ edit, reason: "There is no wall with this id in the scan." });
      continue;
    }
    at.room.walls = at.room.walls.filter((_, i) => i !== at.index);
    at.room.touched = true;
    at.room.runsChanged = true;
    deletedWalls.add(edit.targetId);
    removed.push({ id: edit.targetId, kind: "wall", reason: "deleted" });
  }

  // ---- walls corrected, their openings following
  for (const edit of of("wall", "modify")) {
    const at = findWall(edit.targetId);
    if (!at) {
      skipped.push({
        edit,
        reason: deletedWalls.has(edit.targetId)
          ? "The wall is deleted."
          : "There is no wall with this id in the scan.",
      });
      continue;
    }
    const wall = at.room.walls[at.index] as CapturedSurface;
    const next = correctedWall(wall, edit.data);
    if (typeof next === "string") {
      skipped.push({ edit, reason: next });
      continue;
    }
    replaceWall(at.room, at.index, next.surface);
    if (next.before && next.after) at.room.runsChanged = true;
    status.set(edit.targetId, "modified");
    if (edit.data.thicknessM !== undefined) thicknessM.set(edit.targetId, edit.data.thicknessM);
    if (next.before && next.after) {
      for (const room of rooms) {
        for (const array of ["windows", "doors", "openings"] as const) {
          room[array].forEach((opening, index) => {
            if (opening.parentIdentifier !== edit.targetId) return;
            const moved = followWall(opening, next.before as PlanLine, next.after as PlanLine);
            if (!moved) return;
            replaceOpening(room, array, index, moved);
            followed.add(opening.identifier);
          });
        }
      }
    }
  }

  // ---- walls added
  let addedWalls = 0;
  for (const edit of of("wall", "add")) {
    if (findWall(edit.targetId) || deletedWalls.has(edit.targetId)) {
      skipped.push({ edit, reason: "A wall with this id is already in the scan." });
      continue;
    }
    const room = rooms.find((r) => r.ref === edit.data.roomRef);
    if (!room) {
      skipped.push({ edit, reason: `There is no room "${edit.data.roomRef}" in the scan.` });
      continue;
    }
    const surface = newWall(edit.targetId, room, edit.data);
    if (typeof surface === "string") {
      skipped.push({ edit, reason: surface });
      continue;
    }
    room.walls = [...room.walls, surface];
    room.touched = true;
    room.runsChanged = true;
    status.set(edit.targetId, "added");
    addedWalls += 1;
    elementLabels.set(edit.targetId, `New wall ${addedWalls}`);
    if (edit.data.thicknessM !== undefined) thicknessM.set(edit.targetId, edit.data.thicknessM);
  }

  // ---- openings deleted
  const deletedOpenings = new Set<string>();
  for (const edit of of("opening", "delete")) {
    const at = findOpening(edit.targetId);
    if (!at) {
      skipped.push({ edit, reason: "There is no window, door or opening with this id in the scan." });
      continue;
    }
    at.room[at.array] = at.room[at.array].filter((_, i) => i !== at.index);
    at.room.touched = true;
    deletedOpenings.add(edit.targetId);
    removed.push({ id: edit.targetId, kind: ARRAY_KIND[at.array], reason: "deleted" });
  }

  // ---- openings corrected
  const modifiedOpenings = new Map<string, OpeningModifyData>();
  for (const edit of of("opening", "modify")) modifiedOpenings.set(edit.targetId, edit.data);
  for (const edit of of("opening", "modify")) {
    const at = findOpening(edit.targetId);
    if (!at) {
      skipped.push({
        edit,
        reason: deletedOpenings.has(edit.targetId)
          ? "It is deleted."
          : "There is no window, door or opening with this id in the scan.",
      });
      continue;
    }
    const opening = at.room[at.array][at.index] as CapturedSurface;
    const hostId = edit.data.wallId ?? opening.parentIdentifier ?? undefined;
    const host = hostId ? findWall(hostId) : undefined;
    if (edit.data.wallId && !host) {
      skipped.push({ edit, reason: "The wall it was moved into is not on the plan." });
      continue;
    }
    const hostSurface = host ? (host.room.walls[host.index] as CapturedSurface) : undefined;
    const next = correctedOpening(opening, hostSurface, edit.data);
    if (typeof next === "string") {
      skipped.push({ edit, reason: next });
      continue;
    }
    if (host && host.room !== at.room) {
      // Into a wall of another room: it belongs to that room now.
      at.room[at.array] = at.room[at.array].filter((_, i) => i !== at.index);
      at.room.touched = true;
      host.room[at.array] = [...host.room[at.array], next];
      host.room.touched = true;
    } else {
      replaceOpening(at.room, at.array, at.index, next);
    }
    status.set(edit.targetId, "modified");
    followed.delete(edit.targetId);
  }

  // ---- openings whose wall is gone go with it
  for (const room of rooms) {
    for (const array of ["windows", "doors", "openings"] as const) {
      const keep: CapturedSurface[] = [];
      for (const opening of room[array]) {
        const parent = opening.parentIdentifier;
        if (parent && deletedWalls.has(parent)) {
          removed.push({ id: opening.identifier, kind: ARRAY_KIND[array], reason: "host-deleted" });
          followed.delete(opening.identifier);
          status.delete(opening.identifier);
          room.touched = true;
        } else {
          keep.push(opening);
        }
      }
      room[array] = keep;
    }
  }

  // ---- openings added
  const addedCount: Record<PlanOpeningKind, number> = { door: 0, window: 0, opening: 0 };
  const NEW_NAME: Record<PlanOpeningKind, string> = { door: "New door", window: "New window", opening: "New opening" };
  for (const edit of of("opening", "add")) {
    if (findOpening(edit.targetId) || deletedOpenings.has(edit.targetId)) {
      skipped.push({ edit, reason: "An opening with this id is already in the scan." });
      continue;
    }
    const host = findWall(edit.data.wallId);
    if (!host) {
      skipped.push({ edit, reason: "The wall it is in is not on the plan." });
      continue;
    }
    const surface = newOpening(edit.targetId, host.room.walls[host.index] as CapturedSurface, edit.data, host.room.story);
    if (typeof surface === "string") {
      skipped.push({ edit, reason: surface });
      continue;
    }
    const array = OPENING_ARRAY[edit.data.kind];
    host.room[array] = [...host.room[array], surface];
    host.room.touched = true;
    status.set(edit.targetId, "added");
    addedCount[edit.data.kind] += 1;
    elementLabels.set(edit.targetId, `${NEW_NAME[edit.data.kind]} ${addedCount[edit.data.kind]}`);
  }


  // ---- floors follow their walls
  const floors: FloorOutcome[] = [];
  const floorOutlines = new Map<string, readonly PlanEditPoint[]>();
  for (const room of rooms) {
    if (!room.runsChanged) continue;
    const lines = room.walls.map(surfacePlanLine).filter((l): l is PlanLine => l !== undefined);
    const loop = wallLoop(lines);
    if ("reason" in loop) {
      floors.push({
        roomRef: room.ref,
        outcome: "as-scanned",
        reason: `${loop.reason} The floor is as the scan captured it, so its area may not match the walls.`,
      });
      continue;
    }
    const floor = redrawnFloor(room, loop.points);
    room.floors = [floor];
    room.touched = true;
    floorOutlines.set(floor.identifier, loop.points);
    followed.add(floor.identifier);
    floors.push({ roomRef: room.ref, outcome: "redrawn", areaM2: surfaceArea(floor) });
  }

  const outRooms: CapturedRoom[] = rooms.map((room) => {
    if (!room.touched) return room.original;
    const r = room.original;
    return {
      ...r,
      ...(r.floors !== undefined || room.floors.length > 0 ? { floors: room.floors } : {}),
      // Pinned: a room with no identifier of its own is known by its lowest
      // surface id, and adding or deleting a wall must not change who it is.
      identifier: room.ref,
      walls: room.walls,
      ...(r.windows !== undefined || room.windows.length > 0 ? { windows: room.windows } : {}),
      ...(r.doors !== undefined || room.doors.length > 0 ? { doors: room.doors } : {}),
      ...(r.openings !== undefined || room.openings.length > 0 ? { openings: room.openings } : {}),
    };
  });

  // ---- rooms merged, then split (a designer's "Merge rooms" / "Split room")
  const structured = applyRoomStructure(outRooms, of("room", "modify"), status, skipped);

  // ---- room names, last, so the halves of a split room can be named
  for (const edit of of("room", "modify")) {
    if (edit.data.label === undefined) continue;
    const present = structured.some((r, i) => capturedRoomRef(r, i) === edit.targetId);
    if (!present) {
      // A room merged away or split in two carries its name no further; any
      // other absent room is reported.
      if (edit.data.mergeInto === undefined && edit.data.split === undefined) {
        skipped.push({ edit, reason: "There is no room with this id in the scan." });
      }
      continue;
    }
    roomLabels.set(edit.targetId, edit.data.label);
    status.set(edit.targetId, "modified");
  }

  // Made from the scan's rooms as already split: never split again (room-split.ts).
  settledRooms(structured);
  // A bare room told apart into several comes back as a structure of them.
  const outCapture: RoomPlanCapture =
    "rooms" in capture && Array.isArray((capture as CapturedStructure).rooms)
      ? { ...(capture as CapturedStructure), rooms: structured }
      : structured.length > 1
        ? { rooms: structured }
        : (structured[0] as CapturedRoom);

  return {
    capture: outCapture,
    overrides: {
      elementLabels,
      ...(roomLabels.size > 0 ? { roomLabels } : {}),
      ...(thicknessM.size > 0 ? { thicknessM } : {}),
      ...(floorOutlines.size > 0 ? { floorOutlines } : {}),
    },
    status,
    followed,
    removed,
    skipped,
    roomLabels,
    floors,
  };
}

/** Wall ends this close are one corner of a room's outline. */
export const FLOOR_JOIN_M = 0.05;

/**
 * The closed outline a room's walls make — the corners in order round it — or
 * why they make none.
 *
 * Wall ends within `joinM` are one corner. A wall with a free end (a stub, a
 * partition that meets another wall part-way along) is not part of the room's
 * outline and is set aside, repeatedly, until only walls that meet at both
 * ends remain. What is left must be ONE loop in which every corner joins
 * exactly two walls; anything else (a gap, a wall moved away from its corners,
 * two rooms' worth of walls) has no single outline to give.
 */
export function wallLoop(
  lines: readonly PlanLine[],
  joinM = FLOOR_JOIN_M,
): { readonly points: PlanEditPoint[] } | { readonly reason: string } {
  const corners: { x: number; y: number; n: number }[] = [];
  const cornerOf = (p: PlanEditPoint): number => {
    const found = corners.findIndex((c) => Math.hypot(c.x - p.x, c.y - p.y) <= joinM);
    if (found >= 0) {
      const c = corners[found] as { x: number; y: number; n: number };
      c.x = (c.x * c.n + p.x) / (c.n + 1);
      c.y = (c.y * c.n + p.y) / (c.n + 1);
      c.n += 1;
      return found;
    }
    corners.push({ x: p.x, y: p.y, n: 1 });
    return corners.length - 1;
  };
  const edges = lines
    .map((l) => [cornerOf(l.start), cornerOf(l.end)] as const)
    .filter(([a, b]) => a !== b);
  const alive = edges.map(() => true);
  const degree = (corner: number): number =>
    edges.reduce((d, [a, b], i) => d + (alive[i] && (a === corner || b === corner) ? (a === b ? 2 : 1) : 0), 0);

  // Set aside walls with a free end until none is left.
  for (let changed = true; changed; ) {
    changed = false;
    edges.forEach(([a, b], i) => {
      if (alive[i] && (degree(a) === 1 || degree(b) === 1)) {
        alive[i] = false;
        changed = true;
      }
    });
  }
  const live = edges.filter((_, i) => alive[i]);
  if (live.length < 3) return { reason: "Its walls no longer close round the room." };
  const used = new Set(live.flatMap(([a, b]) => [a, b]));
  if ([...used].some((c) => degree(c) !== 2)) {
    return { reason: "More than two of its walls meet at one corner, so the room has no single outline." };
  }

  // Walk the loop.
  const order: number[] = [live[0]![0]];
  const taken = new Set<number>();
  let at = live[0]![0];
  for (let step = 0; step < live.length; step += 1) {
    const next = live.findIndex(([a, b], i) => !taken.has(i) && (a === at || b === at));
    if (next < 0) break;
    taken.add(next);
    const [a, b] = live[next] as readonly [number, number];
    at = a === at ? b : a;
    if (step < live.length - 1) order.push(at);
  }
  if (taken.size !== live.length || at !== order[0]) {
    return { reason: "Its walls make more than one outline." };
  }
  return { points: order.map((i) => ({ x: (corners[i] as { x: number }).x, y: (corners[i] as { y: number }).y })) };
}

/**
 * A room's floor redrawn to `outline`: one flat surface at the height of the
 * floor it replaces, square to that floor's own axes (world X where it has
 * none), with the outline as its `polygonCorners` so every reader that
 * measures a floor by its polygon (`surfaceArea`, and so `convertCapture`)
 * gets the outline's area. It keeps the first scanned floor's identifier, so
 * a selection or a table row still finds it.
 */
function redrawnFloor(room: MutableRoom, outline: readonly PlanEditPoint[]): CapturedSurface {
  const original = room.floors.find((f) => isUsableTransform(f.transform));
  let a = { x: 1, y: 0 };
  if (original) {
    const w = widthAxisOf(original.transform as readonly number[]);
    const l = Math.hypot(w.x, w.z);
    if (l > 1e-6 && Math.abs(w.y) < 0.2) a = { x: w.x / l, y: w.z / l };
  }
  // The in-plan height axis, turned so the surface's normal (width x height) points up.
  const b = { x: a.y, y: -a.x };
  const us = outline.map((p) => p.x * a.x + p.y * a.y);
  const vs = outline.map((p) => p.x * b.x + p.y * b.y);
  const u0 = Math.min(...us);
  const u1 = Math.max(...us);
  const v0 = Math.min(...vs);
  const v1 = Math.max(...vs);
  const uc = (u0 + u1) / 2;
  const vc = (v0 + v1) / 2;
  const centre = { x: a.x * uc + b.x * vc, y: a.y * uc + b.y * vc };
  const floorY = original ? translationOf(original.transform as readonly number[]).y : roomFloorY(room);
  return {
    identifier: original?.identifier ?? room.floors[0]?.identifier ?? `${room.ref}:floor`,
    dimensions: [u1 - u0, v1 - v0, 0],
    transform: [a.x, 0, a.y, 0, b.x, 0, b.y, 0, 0, 1, 0, 0, centre.x, floorY, centre.y, 1],
    polygonCorners: outline.map((_, i) => [(us[i] as number) - uc, (vs[i] as number) - vc, 0]),
    story: room.story,
  };
}

function inBounds(value: number, range: { readonly min: number; readonly max: number }): boolean {
  return Number.isFinite(value) && value >= range.min && value <= range.max;
}

/** A wall with `data` applied, and where it ran before and after (when its run changed). */
function correctedWall(
  wall: CapturedSurface,
  data: WallModifyData,
): { surface: CapturedSurface; before?: PlanLine; after?: PlanLine } | string {
  if (!isUsableTransform(wall.transform)) return "The wall has no position in the scan to correct.";
  let m = matrixOf(wall);
  let dims: { 0?: number; 1?: number; 2?: number } = {};
  let before: PlanLine | undefined;
  let after: PlanLine | undefined;

  if ((data.start === undefined) !== (data.end === undefined)) {
    return "A wall's run needs both its ends.";
  }
  if (data.start && data.end) {
    before = surfacePlanLine(wall);
    if (!before) return "The wall's run cannot be read from the scan.";
    after = { start: data.start, end: data.end };
    const length = planLineLengthM(after);
    if (!inBounds(length, PLAN_EDIT_LIMITS.wallLengthM)) return `A wall ${length.toFixed(3)} m long is not accepted.`;
    const a0 = unit(before) as PlanEditPoint;
    const a1 = unit(after) as PlanEditPoint;
    const { c, s } = rotationBetween(a0, a1);
    m = turned(m, c, s);
    m = placed(m, (after.start.x + after.end.x) / 2, m[13] ?? 0, (after.start.y + after.end.y) / 2);
    dims = { ...dims, 0: length };
  }
  if (data.heightM !== undefined) {
    if (!inBounds(data.heightM, PLAN_EDIT_LIMITS.wallHeightM)) return "That wall height is not accepted.";
    // Taller or shorter from the bottom edge, which stays on the floor.
    const h0 = heightOfSurface(wall);
    m = placed(m, m[12] ?? 0, (m[13] ?? 0) + (data.heightM - h0) / 2, m[14] ?? 0);
    dims = { ...dims, 1: data.heightM };
  }
  if (data.thicknessM !== undefined) {
    if (!inBounds(data.thicknessM, PLAN_EDIT_LIMITS.wallThicknessM)) return "That wall thickness is not accepted.";
    dims = { ...dims, 2: data.thicknessM };
  }
  return {
    surface: { ...wall, transform: m, dimensions: dimensionsWith(wall, dims) },
    ...(before && after ? { before, after } : {}),
  };
}

/** An opening carried along with its wall. */
function followWall(opening: CapturedSurface, before: PlanLine, after: PlanLine): CapturedSurface | undefined {
  if (!isUsableTransform(opening.transform)) return undefined;
  const c = translationOf(opening.transform);
  const width = opening.dimensions?.[0];
  const half = typeof width === "number" && width > 0 ? width / 2 : 0;
  const moved = relocateOnWall({ x: c.x, y: c.z }, before, after, half);
  const a0 = unit(before) as PlanEditPoint;
  const a1 = unit(after) as PlanEditPoint;
  const { c: cos, s: sin } = rotationBetween(a0, a1);
  const m = placed(turned(matrixOf(opening), cos, sin), moved.point.x, c.y, moved.point.y);
  return { ...opening, transform: m };
}

/** An opening with `data` applied, in `host` (its wall, possibly a new one). */
function correctedOpening(
  opening: CapturedSurface,
  host: CapturedSurface | undefined,
  data: OpeningModifyData,
): CapturedSurface | string {
  if (!isUsableTransform(opening.transform)) return "It has no position in the scan to correct.";
  const w0 = opening.dimensions?.[0] ?? 0;
  const h0 = heightOfSurface(opening);
  const width = data.widthM ?? w0;
  const height = data.heightM ?? h0;
  if (data.widthM !== undefined && !inBounds(data.widthM, PLAN_EDIT_LIMITS.openingWidthM)) return "That width is not accepted.";
  if (data.heightM !== undefined && !inBounds(data.heightM, PLAN_EDIT_LIMITS.openingHeightM)) return "That height is not accepted.";
  if (data.sillHeightM !== undefined && !inBounds(data.sillHeightM, PLAN_EDIT_LIMITS.sillHeightM)) return "That sill height is not accepted.";

  let m = matrixOf(opening);
  const centre = translationOf(m);
  const hostLine = host ? surfacePlanLine(host) : undefined;
  const rehosted = data.wallId !== undefined && data.wallId !== opening.parentIdentifier;

  if (hostLine && (rehosted || data.offsetM !== undefined || data.widthM !== undefined)) {
    const along = unit(hostLine) as PlanEditPoint;
    const L = planLineLengthM(hostLine);
    const here = { x: centre.x, y: centre.z };
    const s0 = data.offsetM ?? alongLineM(here, hostLine);
    const s = L >= width ? clamp(s0, width / 2, L - width / 2) : L / 2;
    // Off the line as it was in its own wall; on the line in a new one.
    const rel = sub(here, hostLine.start);
    const off = rehosted ? 0 : rel.x * -along.y + rel.y * along.x;
    const p = pointAlongLine(hostLine, s);
    if (rehosted && host && isUsableTransform(host.transform)) {
      // Take the new wall's axes: the opening now lies in that wall's plane.
      const hm = matrixOf(host);
      m = [...hm];
    }
    m = placed(m, p.x + -along.y * off, centre.y, p.y + along.x * off);
  }

  // Height and sill, measured from the bottom of the wall it is in. Where
  // that cannot be read, the opening's own bottom edge stays put.
  if (data.sillHeightM !== undefined || data.heightM !== undefined || rehosted) {
    const floor = host ? bottomY(host) : undefined;
    const ownBottom = bottomY(opening);
    let y: number;
    if (floor !== undefined) {
      const sill = data.sillHeightM ?? (ownBottom !== undefined ? ownBottom - floor : 0);
      y = floor + sill + height / 2;
    } else if (ownBottom !== undefined) {
      y = ownBottom + height / 2;
    } else {
      y = centre.y + (height - h0) / 2;
    }
    m = placed(m, m[12] ?? 0, y, m[14] ?? 0);
  }

  return {
    ...opening,
    transform: m,
    dimensions: dimensionsWith(opening, { 0: width, 1: height }),
    ...(host ? { parentIdentifier: host.identifier } : {}),
  };
}

/** The floor of a room, world Y, from the bottoms of its walls (or its floor surface). */
function roomFloorY(room: MutableRoom): number {
  const bases = room.walls.map(bottomY).filter((y): y is number => y !== undefined);
  const fromWalls = median(bases);
  if (fromWalls !== undefined) return fromWalls;
  const floor = (room.original.floors ?? []).find((f) => isUsableTransform(f.transform));
  return floor ? translationOf(floor.transform as readonly number[]).y : 0;
}

function newWall(id: string, room: MutableRoom, data: WallAddData): CapturedSurface | string {
  const line: PlanLine = { start: data.start, end: data.end };
  const length = planLineLengthM(line);
  if (!inBounds(length, PLAN_EDIT_LIMITS.wallLengthM)) return `A wall ${length.toFixed(3)} m long is not accepted.`;
  const tallest = Math.max(0, ...room.walls.map(heightOfSurface));
  const height = data.heightM ?? (tallest > 0 ? tallest : DEFAULT_WALL_HEIGHT_M);
  if (!inBounds(height, PLAN_EDIT_LIMITS.wallHeightM)) return "That wall height is not accepted.";
  if (data.thicknessM !== undefined && !inBounds(data.thicknessM, PLAN_EDIT_LIMITS.wallThicknessM)) {
    return "That wall thickness is not accepted.";
  }
  const a = unit(line) as PlanEditPoint;
  const floor = roomFloorY(room);
  const mid = { x: (line.start.x + line.end.x) / 2, y: (line.start.y + line.end.y) / 2 };
  // Width along the run, height straight up, normal = width x height.
  const transform = [a.x, 0, a.y, 0, 0, 1, 0, 0, -a.y, 0, a.x, 0, mid.x, floor + height / 2, mid.y, 1];
  return {
    identifier: id,
    dimensions: [length, height, data.thicknessM ?? 0],
    transform,
    story: room.story,
  };
}

function newOpening(
  id: string,
  host: CapturedSurface,
  data: OpeningAddData,
  story: number,
): CapturedSurface | string {
  const line = surfacePlanLine(host);
  if (!line || !isUsableTransform(host.transform)) return "The wall it is in has no position on the plan.";
  if (!inBounds(data.widthM, PLAN_EDIT_LIMITS.openingWidthM)) return "That width is not accepted.";
  if (!inBounds(data.heightM, PLAN_EDIT_LIMITS.openingHeightM)) return "That height is not accepted.";
  if (!inBounds(data.sillHeightM, PLAN_EDIT_LIMITS.sillHeightM)) return "That sill height is not accepted.";
  const L = planLineLengthM(line);
  const s = L >= data.widthM ? clamp(data.offsetM, data.widthM / 2, L - data.widthM / 2) : L / 2;
  const p = pointAlongLine(line, s);
  const floor = bottomY(host) ?? translationOf(host.transform).y - heightOfSurface(host) / 2;
  const m = placed(matrixOf(host), p.x, floor + data.sillHeightM + data.heightM / 2, p.y);
  return {
    identifier: id,
    dimensions: [data.widthM, data.heightM, 0],
    transform: m,
    parentIdentifier: host.identifier,
    story,
    ...(data.kind === "opening" ? { category: "opening" } : {}),
  };
}

// ------------------------------------------------- staleness, for heat loss ----

/**
 * True for edits that change what heat loss reads: a wall's or opening's size
 * or place, or a room split or merged. A room's NAME does not — it is outside
 * the signature, as it always was.
 */
export function affectsGeometry(edit: Pick<PlanEdit, "targetKind"> & { readonly data?: unknown }): boolean {
  if (edit.targetKind === "wall" || edit.targetKind === "opening") return true;
  const data = edit.data as RoomModifyData | undefined;
  return edit.targetKind === "room" && (data?.split !== undefined || data?.mergeInto !== undefined);
}

/** What of an edit the signature reads: for a room, only its split and merge, never its name. */
function signed(edit: PlanEdit): unknown {
  if (edit.targetKind !== "room") return edit.data ?? {};
  return {
    ...(edit.data.split !== undefined ? { split: edit.data.split } : {}),
    ...(edit.data.mergeInto !== undefined ? { mergeInto: edit.data.mergeInto } : {}),
  };
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .filter((k) => record[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonical(record[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

/**
 * A fingerprint of the geometry the edit layer describes — the same string for
 * the same walls and openings whatever order the records come in, and "" for
 * none (the scan as captured).
 *
 * For the heat loss: a calculation made from the effective plan records the
 * signature it was made from; the plan has been "edited since the heat loss
 * was calculated" exactly when today's signature differs. A heat loss made
 * from the raw scan was made at signature "".
 */
export function planEditsSignature(edits: readonly PlanEdit[]): string {
  const rows = edits
    .filter(affectsGeometry)
    .map((e) => canonical({ k: e.targetKind, i: e.targetId, a: e.action, d: signed(e) }))
    .sort();
  return rows.length === 0 ? "" : rows.join("\n");
}

/** Has the geometry moved on from the signature a calculation was made at? */
export function planEditedSince(edits: readonly PlanEdit[], calculatedAtSignature: string): boolean {
  return planEditsSignature(edits) !== calculatedAtSignature;
}

// ------------------------------------------------- the plan, corrected ----

/** The effective plan's rooms and elements, named as the edit layer says. */
export function effectiveRoomGroups(plan: EffectivePlan): readonly ScanRoomGroup[] {
  return scanRoomGroups(plan.capture, plan.overrides);
}

/** The effective plan projected to plan — what the floor plan draws and the design check reads. */
export function effectivePlanRooms(plan: EffectivePlan): readonly PlanRoom[] {
  return scanPlanRooms(effectiveRoomGroups(plan));
}
